import { useState, useEffect } from 'react';
import { Modal, Select, Form, Input, message, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Button, Card, Text } from '@bancobarigui/barihaus';

import { userService } from '@/services/user.service';
import { inspectionAssignmentService } from '@/services/inspectionAssignment.service';
import { useAuth } from '@/shared/hooks/useAuth';
import { Inspector } from '@/services/types/user.types';

interface AssignInspectorModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  providerId?: string;
  onSuccess?: () => void;
}

export function AssignInspectorModal({
  open,
  onClose,
  assignmentId,
  providerId,
  onSuccess,
}: AssignInspectorModalProps) {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const targetProviderId = providerId || user?.providerId;

    if (open && targetProviderId) {
      loadInspectors();
    }
  }, [open, user?.providerId, providerId]);

  const loadInspectors = async () => {
    const targetProviderId = providerId || user?.providerId;

    if (!targetProviderId) {
      message.error('Não foi possível identificar a prestadora');
      return;
    }

    setLoading(true);
    try {
      const data = await userService.getInspectorsByProvider({
        providerId: targetProviderId,
        page: 1,
        size: 50
      });

      const inspectorsToSet = Array.isArray(data) ? data : [];
      setInspectors(inspectorsToSet);
    } catch {
      message.error('Erro ao carregar lista de inspetores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: { inspectorId: string; notes: string }) => {
    setSubmitting(true);
    try {
      await inspectionAssignmentService.assignInspector(assignmentId, {
        inspectorId: values.inspectorId,
        notes: values.notes || 'Inspetor designado via sistema'
      });

      message.success('Inspeção direcionada para o inspetor com sucesso!');
      form.resetFields();
      onSuccess?.();
      onClose();
    } catch {
      message.error('Erro ao direcionar inspeção para o inspetor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <UserOutlined className="text-blue-500" />
          <Text strong>Direcionar Inspeção para Inspetor</Text>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className="py-4">
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <UserOutlined className="text-blue-500 mt-1" />
            <div>
              <Text strong className="text-blue-900 block mb-1">
                Direcionamento de Inspeção
              </Text>
              <Text className="text-blue-700 text-sm">
                Selecione um inspetor ativo da sua prestadora para realizar esta inspeção. 
                Você pode adicionar observações sobre a escolha do inspetor.
              </Text>
            </div>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="inspectorId"
            label="Inspetor"
            rules={[
              { required: true, message: 'Por favor, selecione um inspetor' }
            ]}
          >
            <Select
              placeholder="Selecione um inspetor"
              loading={loading}
              size="large"
              showSearch
              optionFilterProp="children"
              notFoundContent={
                loading ? <Spin size="small" /> : (
                  inspectors.length === 0 ? 
                    'Nenhum inspetor ativo encontrado' : 
                    'Nenhum inspetor encontrado'
                )
              }
            >
              {inspectors.map((inspector) => (
                <Select.Option key={inspector.id} value={inspector.userId}>
                  {inspector.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Observações (opcional)"
            extra="Adicione informações sobre o motivo da escolha deste inspetor"
          >
            <Input.TextArea
              placeholder="Ex: Inspetor escolhido por experiência na região e disponibilidade."
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item className="mb-0 pt-4">
            <div className="flex gap-3 justify-end">
              <Button 
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                buttonColor="primary"
                htmlType="submit"
                loading={submitting}
                disabled={loading || inspectors.length === 0}
              >
                Direcionar Inspeção
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}